import React from "react";
import { Download, Check } from "lucide-react";
import { jsPDF } from "jspdf";
import { QuizResult, ContactInfo } from "@/types";
import { toast } from "@/hooks/use-toast";
import { generateAIGovernanceReport } from "@/utils/pdf/pdfGenerator";

interface ReportSuccessProps {
  result: QuizResult;
  contactInfo: ContactInfo;
}

const ReportSuccess: React.FC<ReportSuccessProps> = ({ result, contactInfo }) => {
  const getReportFileName = (): string => {
    const companySlug = contactInfo.company.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return `ai-governance-report-${companySlug}.pdf`;
  };

  const generateReport = (): jsPDF => {
    try {
      return generateAIGovernanceReport(result, contactInfo);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Generation failed",
        description: "There was a problem generating your report. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const downloadReport = () => {
    try {
      const pdf = generateReport();
      pdf.save(getReportFileName());
      
      toast({
        title: "Download started",
        description: "Your AI Governance report is being downloaded.",
      });
    } catch (error) {
      // Error already handled in generateReport
    }
  };

  return (
    <div className="border-t border-gray-200 pt-8 text-center">
      <div className="bg-green-50 border border-green-100 rounded-lg p-6 mb-6 inline-block">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600 mb-4">
          Your detailed report for <strong>{contactInfo.company}</strong> is ready.
        </p>
        
        <div className="flex justify-center">
          <button 
            onClick={downloadReport}
            className="btn-secondary inline-flex items-center gap-2 px-6 py-3 text-base"
          >
            <Download className="w-5 h-5" />
            Download Report
          </button>
        </div>
      </div>
      
      <div className="max-w-lg mx-auto">
        <h3 className="text-xl font-semibold mb-4">Next Steps</h3>
        <p className="text-gray-600">
          Our team will review your assessment and may reach out with additional resources to help improve your board's AI governance.
        </p>
      </div>
    </div>
  );
};

export default ReportSuccess;
